/**
 * PlayFirstSuikamonPopup.tsx
 * A friendly, global "start playing" popup shown to anyone browsing the app who doesn't own a
 * Suikamon yet. It's the app-wide onboarding nudge — mounted once in AppContent (above
 * <Routes>) so it can appear on any page.
 *
 * The CTA says **Play**, never "Hatch": hatching is a later in-game step (warming an egg you
 * already own), so a first-time visitor reads "hatch" as some other, unrelated action. The
 * single CTA routes to the free starter flow (/play-free) — no payment, no sign-up —
 * and pre-picks the species when the visitor is on a species landing page.
 *
 * Identity/UX only — it never mints or touches custody.
 * Mirrors the SeasonPopup / WelcomeActivationModal pattern (custom overlay + localStorage guard),
 * but is keyed by the acting address with a cooldown so it stays a nudge, not a nag.
 */
import { useEffect } from "react";
import type { SuikamonSpecies } from "@shared/domain/petModels";
import { getFreePreviewImage, isPlayableForFree } from "../utils/playFreeLink";
import "./PlayFirstSuikamonPopup.css";
import { EGG_IMAGE_SRC } from "../utils/petHelpers";
import { useT } from "../i18n/i18nContext";

/** localStorage key prefix → one guard entry per acting address (`<prefix><address>`). */
const PLAY_POPUP_STORAGE_PREFIX = "suikamon.playFirstPopup.v1.";

/** After a dismiss we hold off re-showing for this long, so the nudge never becomes a nag. */
const PLAY_POPUP_COOLDOWN_MS = 12 * 60 * 60 * 1000; // 12 hours

/** Reused public/ art — no new assets. */
const PLAY_POPUP_EGG_SRC = EGG_IMAGE_SRC;

function playPopupKey(address: string): string {
  return `${PLAY_POPUP_STORAGE_PREFIX}${address}`;
}

/** True if the play nudge hasn't been dismissed for this address within the cooldown window. */
// eslint-disable-next-line react-refresh/only-export-components -- intentional co-located export
export function shouldShowPlayPopup(address: string): boolean {
  try {
    const raw = localStorage.getItem(playPopupKey(address));
    if (!raw) return true;
    const dismissedAt = Number(raw);
    if (!Number.isFinite(dismissedAt)) return true;
    return Date.now() - dismissedAt > PLAY_POPUP_COOLDOWN_MS;
  } catch {
    // localStorage unavailable → don't risk showing the popup repeatedly.
    return false;
  }
}

/** Record that the play nudge was dismissed now, starting the cooldown. */
// eslint-disable-next-line react-refresh/only-export-components -- intentional co-located export
export function markPlayPopupShown(address: string): void {
  try {
    localStorage.setItem(playPopupKey(address), String(Date.now()));
  } catch {
    // Best-effort — if persistence fails the dismiss still closes the popup this session.
  }
}

interface PlayFirstSuikamonPopupProps {
  /** Species landing page the visitor is on, so the copy and the CTA can name that Suikamon. */
  species?: SuikamonSpecies | null;
  /** Primary CTA → the free starter flow. */
  onPlay: () => void;
  /** Dismiss without playing ("Maybe later" / backdrop / close). */
  onClose: () => void;
}

export function PlayFirstSuikamonPopup({ species, onPlay, onClose }: PlayFirstSuikamonPopupProps) {
  const t = useT();
  // Close on Escape for keyboard users (matches the app's other custom-overlay popups).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const target = species && isPlayableForFree(species) ? species : null;
  const title = target ? t("care.playFirst.titleSpecies", { species: target.name }) : t("care.playFirst.title");
  const subtitle = target
    ? t("care.playFirst.subtitleSpecies", { species: target.name })
    : t("care.playFirst.subtitle");
  const artwork = target ? getFreePreviewImage(target) : PLAY_POPUP_EGG_SRC;
  const artworkAlt = target ? t("care.playFirst.artSpecies", { species: target.name }) : t("care.playFirst.artEgg");

  return (
    <div className="play-first-popup-overlay" onClick={onClose} role="presentation">
      <div
        className="play-first-popup-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="play-first-popup-title"
      >
        <button className="play-first-popup-close" onClick={onClose} aria-label={t("common.action.close")}>
          ✕
        </button>

        <div className="play-first-popup-egg-stage">
          <span className="play-first-popup-egg-glow" aria-hidden="true" />
          <img src={artwork} alt={artworkAlt} className="play-first-popup-egg" />
        </div>

        <p className="play-first-popup-eyebrow">{t("care.playFirst.eyebrow")}</p>
        <h2 id="play-first-popup-title" className="play-first-popup-title">
          {title}
        </h2>
        <p className="play-first-popup-subtitle">{subtitle}</p>

        <div className="play-first-popup-actions">
          <button className="play-first-popup-cta" onClick={onPlay}>
            {t("care.playFirst.play")}
          </button>
          <button className="play-first-popup-later" onClick={onClose}>
            {t("care.playFirst.later")}
          </button>
        </div>
      </div>
    </div>
  );
}
